import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import { useIntl } from 'react-intl';
import {
    COURSE_STAGE_VALUES,
    courseStageLabelId,
    type CourseStage,
} from './model';

type CourseStageFilterProps = {
    /** The stage the list is narrowed to, or undefined for every patient. */
    selected: CourseStage | undefined;
    onChange: (stage: CourseStage | undefined) => void;
};

/** The group value standing for "every patient", beside the stage ids. */
const ALL = 'all';

/**
 * The course-stage filter: one toggle per course stage, in course order,
 * behind an "All" toggle. Exactly one is
 * pressed at a time, so the list is always either the whole cohort or one
 * stage of it, and the pressed toggle itself is the visible statement of
 * that filter.
 */
export default function CourseStageFilter({
    selected,
    onChange,
}: CourseStageFilterProps) {
    const intl = useIntl();
    return (
        <ToggleButtonGroup
            exclusive
            size="small"
            value={selected ?? ALL}
            onChange={(_event, value: CourseStage | typeof ALL | null) => {
                // Re-pressing the pressed toggle yields null; the filter is
                // never "no stage and not all", so that press changes nothing.
                if (value !== null) {
                    onChange(value === ALL ? undefined : value);
                }
            }}
            aria-label={intl.formatMessage({
                id: 'patientList.stageFilter.strip',
            })}
        >
            <ToggleButton value={ALL}>
                {intl.formatMessage({ id: 'patientList.stageFilter.all' })}
            </ToggleButton>
            {COURSE_STAGE_VALUES.map((stage) => (
                <ToggleButton key={stage} value={stage}>
                    {intl.formatMessage({ id: courseStageLabelId(stage) })}
                </ToggleButton>
            ))}
        </ToggleButtonGroup>
    );
}
